/**
 * translateAIMetadata — Traduit les champs d'ai_metadata du FR vers l'EN.
 *
 * Pourquoi côté front et pas Claude :
 *   Claude renvoie ses analyses en FR (prompt en FR avec valeurs énumérées).
 *   Plutôt que re-générer en EN (coûte $$ + nouvelle analyse), on traduit
 *   à la volée via un dictionnaire fermé. Le vocabulaire fashion analysé
 *   par Claude est un set fini → couverture 100%.
 *
 * Usage :
 *   import { translateMeta, translateField } from '../utils/translateAIMetadata';
 *   const enColor = translateField('primaryColor', m.primaryColor, 'en');
 *   const translated = translateMeta(m, 'en'); // objet entier
 *
 * v1.12.331
 */

const NOOP = (v) => v;

// ─── Dictionnaires FR → EN ──────────────────────────────────────
const COLOR = {
  noir: 'black', blanc: 'white', beige: 'beige', bleu: 'blue',
  marine: 'navy', rouge: 'red', rose: 'pink', vert: 'green',
  gris: 'gray', marron: 'brown', camel: 'camel', kaki: 'khaki',
  bordeaux: 'burgundy', violet: 'purple', jaune: 'yellow',
  orange: 'orange', argent: 'silver', doré: 'gold', dore: 'gold',
  ivoire: 'ivory', nude: 'nude', multi: 'multi', écru: 'ecru',
  ecru: 'ecru', taupe: 'taupe', menthe: 'mint', olive: 'olive',
  émeraude: 'emerald', pistache: 'pistachio', ciel: 'sky blue',
  denim: 'denim', azur: 'azure', pétrole: 'petrol', framboise: 'raspberry',
  cerise: 'cherry', cognac: 'cognac', chocolat: 'chocolate',
  caramel: 'caramel', sable: 'sand', champagne: 'champagne',
  poudré: 'powder pink', fuchsia: 'fuchsia', saumon: 'salmon',
  souris: 'mouse gray', perle: 'pearl', anthracite: 'anthracite',
  moutarde: 'mustard', ocre: 'ochre', safran: 'saffron',
  lavande: 'lavender', prune: 'plum', lilas: 'lilac',
};

const MATERIAL = {
  coton: 'cotton', laine: 'wool', soie: 'silk', cuir: 'leather',
  denim: 'denim', lin: 'linen', velours: 'velvet', polyester: 'polyester',
  cachemire: 'cashmere', synthétique: 'synthetic', synthetique: 'synthetic',
  'métal doré': 'gold metal', 'metal dore': 'gold metal',
  satin: 'satin', dentelle: 'lace', tweed: 'tweed', viscose: 'viscose',
  nylon: 'nylon', mohair: 'mohair', maille: 'knit', fourrure: 'fur',
  daim: 'suede', suède: 'suede', toile: 'canvas', jersey: 'jersey',
};

const PATTERN = {
  uni: 'solid', rayé: 'striped', raye: 'striped',
  imprimé: 'printed', imprime: 'printed',
  fleuri: 'floral', géométrique: 'geometric', geometrique: 'geometric',
  'à pois': 'polka dot', 'a pois': 'polka dot',
  carreaux: 'checked', léopard: 'leopard', leopard: 'leopard',
  'tie-dye': 'tie-dye',
};

const FIT = {
  ajusté: 'fitted', ajuste: 'fitted',
  droit: 'straight', oversize: 'oversized',
  évasé: 'flared', evase: 'flared',
  cintré: 'tailored', cintre: 'tailored',
  structuré: 'structured', structure: 'structured',
  slim: 'slim', baggy: 'baggy', regular: 'regular',
};

const LENGTH = {
  court: 'short', 'mi-long': 'midi', long: 'long',
  maxi: 'maxi', standard: 'standard',
};

const NECKLINE = {
  rond: 'round', V: 'V', carré: 'square', carre: 'square',
  bateau: 'boat', 'dos-nu': 'backless',
  'col-chemise': 'shirt collar', roulé: 'turtleneck',
  roule: 'turtleneck', bénitier: 'cowl', benitier: 'cowl',
};

const SLEEVE = {
  'sans-manches': 'sleeveless', courte: 'short', '3/4': '3/4', longue: 'long',
};

const TONE = {
  chaud: 'warm', froid: 'cool', neutre: 'neutral',
};

const COLOR_FAMILY = {
  neutral: 'neutral', warm: 'warm', cool: 'cool',
  pastel: 'pastel', vivid: 'vivid',
};

const SEASONS = {
  printemps: 'spring', été: 'summer', ete: 'summer',
  automne: 'autumn', hiver: 'winter',
};

const TIME_OF_DAY = {
  jour: 'daytime', soir: 'evening', 'toute-heure': 'anytime',
};

const PRICE_RANGE = {
  budget: 'budget', 'mid-range': 'mid-range', premium: 'premium', luxury: 'luxury',
};

const OCCASION = {
  bureau: 'office', soirée: 'evening', soiree: 'evening',
  cocktail: 'cocktail', weekend: 'weekend', date: 'date',
  sport: 'sport', vacances: 'vacation',
  cérémonie: 'ceremony', ceremonie: 'ceremony',
  quotidien: 'daily',
};

const STYLE = {
  chic: 'chic', casual: 'casual',
  romantique: 'romantic', bohème: 'boho', boheme: 'boho',
  sport: 'sport', streetwear: 'streetwear',
  élégant: 'elegant', elegant: 'elegant',
  minimaliste: 'minimalist', workwear: 'workwear',
  vintage: 'vintage', edgy: 'edgy', preppy: 'preppy',
};

// Tags de style courts (mélange de styles, formalité, etc.)
const STYLE_TAGS = {
  ...STYLE,
  formel: 'formal', décontracté: 'relaxed', decontracte: 'relaxed',
  sophistiqué: 'sophisticated', sophistique: 'sophisticated',
  rétro: 'retro', retro: 'retro', moderne: 'modern',
  intemporel: 'timeless', tendance: 'trendy', luxueux: 'luxurious',
  confortable: 'comfortable', féminin: 'feminine', feminin: 'feminine',
  masculin: 'masculine', androgyne: 'androgynous',
  audacieux: 'bold', épuré: 'minimal', epure: 'minimal',
  raffiné: 'refined', raffine: 'refined',
};

// ─── Mapping field → dictionary ─────────────────────────────────
const FIELD_DICT = {
  primaryColor:     COLOR,
  colorDetail:      COLOR,
  secondaryColors:  COLOR,
  colorTone:        TONE,
  colorFamily:      COLOR_FAMILY,
  material:         MATERIAL,
  pattern:          PATTERN,
  fit:              FIT,
  silhouette:       FIT,
  length:           LENGTH,
  neckline:         NECKLINE,
  sleeveLength:     SLEEVE,
  seasonality:      SEASONS,
  timeOfDay:        TIME_OF_DAY,
  priceRange:       PRICE_RANGE,
  styleTags:        STYLE_TAGS,
};

/** Traduit une valeur unique avec son dictionnaire field-spécifique. */
export function translateField(field, value, locale = 'fr') {
  if (locale !== 'en') return value;
  if (value == null) return value;
  const dict = FIELD_DICT[field];
  if (!dict) return value;
  const key = String(value).toLowerCase().trim();
  return dict[key] || value;
}

/** Traduit un tableau de valeurs (utile pour seasonality, secondaryColors, styleTags). */
export function translateArray(field, arr, locale = 'fr') {
  if (locale !== 'en') return arr;
  if (!Array.isArray(arr)) return arr;
  return arr.map(v => translateField(field, v, locale));
}

/**
 * Retourne une copie de l'objet metadata avec tous les champs traduits.
 * Préserve les champs non listés (nombres, booléens, scores).
 */
export function translateMeta(meta, locale = 'fr') {
  if (locale !== 'en' || !meta || typeof meta !== 'object') return meta;
  const out = { ...meta };
  for (const [field, dict] of Object.entries(FIELD_DICT)) {
    if (out[field] == null) continue;
    if (Array.isArray(out[field])) {
      out[field] = translateArray(field, out[field], 'en');
    } else {
      out[field] = translateField(field, out[field], 'en');
    }
  }
  // styleScores et occasions : clés FR à traduire mais valeurs (scores) intactes
  if (out.styleScores && typeof out.styleScores === 'object') {
    const translated = {};
    for (const [k, v] of Object.entries(out.styleScores)) {
      const enKey = STYLE[k.toLowerCase()] || k;
      translated[enKey] = v;
    }
    out.styleScores = translated;
  }
  if (out.occasions && typeof out.occasions === 'object') {
    const translated = {};
    for (const [k, v] of Object.entries(out.occasions)) {
      const enKey = OCCASION[k.toLowerCase()] || k;
      translated[enKey] = v;
    }
    out.occasions = translated;
  }
  // Nom : si nameEn existe, l'utiliser à la place de name
  if (out.nameEn && typeof out.nameEn === 'string') {
    out._displayName = out.nameEn;
  }
  return out;
}

export default { translateField, translateArray, translateMeta };
